// src/components/invoices/InvoicePreviewModal.jsx
import React, { useState, useCallback, useMemo, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { searchCuitWithFallback, saveClientToDatabase } from '../../services/cuitService';
import { requiresIIBBRetention, getDetailedIIBBStatus } from '../../services/iibbService';
import { budgetQuoteService } from '../../services/budgetQuoteService';
import { runningAccountService } from '../../services/runningAccountService';
import { INVOICE_FEATURES, FLASK_CLIPBOARD_API_URL } from '../../config';
import { createInvoiceV2 } from '../../services/invoiceService';
import { mapResponseToAlert, mapErrorToAlert } from '../../utils/arcaErrorHelper';
import CartContext from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import ClientSelectionModal from './ClientSelectionModal';
import BudgetQuoteSuccessModal from './BudgetQuoteSuccessModal';
import RunningAccountSuccessModal from './RunningAccountSuccessModal';
import SplitPaymentSelector, { getPaymentMethodName } from './SplitPaymentSelector';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Button,
    Box,
    Divider,
    Alert,
    IconButton,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    FormControlLabel,
    Radio,
    RadioGroup,
    Chip
} from '@mui/material';
import {
    Close as CloseIcon,
    Receipt as ReceiptIcon,
    Send as SendIcon,
    Search as SearchIcon
} from '@mui/icons-material';

// Helper to map API response to localized message and color.
// Delegado al helper central para mantener el mismo catálogo de errores
// que el camino "Facturar a Tercero" y las Notas de Crédito.
function getInvoiceUserMessage(response) {
    const t = mapResponseToAlert(response);
    if (!t) return null;
    // Compatibilidad con el shape anterior que el modal espera
    // (campo `message` en vez de title/mensaje/accion separados).
    const message = [t.title, t.mensaje, t.accion ? `→ ${t.accion}` : '', t.code ? `(${t.code})` : '']
        .filter(Boolean)
        .join('\n');
    return {
        type: t.type,
        color: t.color,
        message,
        // Campos extra para UI rica:
        title: t.title,
        accion: t.accion,
        code: t.code
    };
}

function InvoicePreviewModal({ 
    open, 
    onClose, 
    cartData,
    getDiscountBreakdown, // NEW: Discount breakdown function
    onConfirmGeneration,
    onClearCart,
    isAlreadyInvoiced = false,
    lastInvoiceNumber = null,
}) {
    // Early return if no cart data
    if (!cartData) {
        return null;
    }

    // Destructure cart data at the top
    const { cartDetails = [], subTotalPrice = 0, totalAfterDiscount = 0, discountPercentage = 0 } = cartData;

    // Get DUAL calculation state from context (NEW!)
    const { dualCalculation, isVolumePricingActive, getVolumePrice, getDisplayRetailPrice } = useContext(CartContext) || {};
    
    // Backwards compatibility: also support V2 calculation
    const { v2Calculation, isCalculatingV2, v2Error } = useContext(CartContext) || {};

    // Calculate accurate discount breakdown using DUAL/V2 backend (MANDATORY)
    const breakdown = useMemo(() => {
        // Priority 1: Use DUAL calculation if available (frontend + backend)
        if (dualCalculation?.frontendCalculation) {
            const useFrontend = !dualCalculation.isValidated;
            const calc = useFrontend ? dualCalculation.frontendCalculation : dualCalculation.backendCalculation;
            
            console.log('InvoicePreviewModal - Using DUAL calculation:', {
                source: useFrontend ? 'frontend-instant' : 'backend-validated',
                isValidating: dualCalculation.isValidating,
                isValidated: dualCalculation.isValidated,
                mismatchDetected: dualCalculation.mismatchDetected
            });
            
            return {
                subtotalWithIVA: parseFloat(calc.subtotalSinIva) * 1.21,
                subtotalWithoutIVA: parseFloat(calc.subtotalSinIva),
                discountAmount: parseFloat(calc.discount),
                discountAmountSimple: parseFloat(calc.discount), // Already calculated on subtotal sin IVA
                subtotalAfterDiscount: parseFloat(calc.subtotalAfterDiscount),
                ivaAmount: parseFloat(calc.iva),
                finalTotal: parseFloat(calc.total),
                calculationSource: useFrontend ? 'frontend-instant' : 'backend-validated',
                isCalculating: dualCalculation.isValidating,
                error: dualCalculation.validationError,
                // DUAL-specific metadata
                isDual: true,
                isValidated: dualCalculation.isValidated,
                mismatchDetected: dualCalculation.mismatchDetected,
                mismatchDetails: dualCalculation.mismatchDetails,
                validationBadge: dualCalculation.validationBadge
            };
        }
        
        // Priority 2: Use V2 backend calculation (legacy compatibility)
        if (v2Calculation) {
            console.log('InvoicePreviewModal - Using V2 backend calculation (legacy)');
            return {
                subtotalWithIVA: parseFloat(v2Calculation.subtotalSinIva) * 1.21,
                subtotalWithoutIVA: parseFloat(v2Calculation.subtotalSinIva),
                discountAmount: parseFloat(v2Calculation.discount),
                discountAmountSimple: parseFloat(v2Calculation.discount), // Already calculated on subtotal sin IVA
                subtotalAfterDiscount: parseFloat(v2Calculation.subtotalAfterDiscount),
                ivaAmount: parseFloat(v2Calculation.iva),
                finalTotal: parseFloat(v2Calculation.total),
                calculationSource: 'backend-v2',
                isCalculating: isCalculatingV2,
                error: v2Error,
                isDual: false
            };
        }
        
        // No calculation available - return loading state
        console.warn('InvoicePreviewModal - Waiting for calculation');
        return {
            subtotalWithIVA: subTotalPrice,
            discountAmount: 0,
            finalTotal: subTotalPrice,
            calculationSource: 'none',
            isCalculating: dualCalculation?.isValidating || isCalculatingV2 || true,
            error: dualCalculation?.validationError || v2Error || 'Waiting for calculation...',
            isDual: false
        };
    }, [dualCalculation, v2Calculation, isCalculatingV2, v2Error, subTotalPrice]);

    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState('');
    const [invoiceResponse, setInvoiceResponse] = useState(null);
    
    // Document type state - INVOICE or BUDGET_QUOTE
    const [documentType, setDocumentType] = useState('INVOICE');
    
    // Budget quote success modal state
    const [isBudgetQuoteSuccessOpen, setIsBudgetQuoteSuccessOpen] = useState(false);
    const [budgetQuoteData, setBudgetQuoteData] = useState(null);
    
    // Running account success modal state
    const [isRunningAccountSuccessOpen, setIsRunningAccountSuccessOpen] = useState(false);
    const [runningAccountSuccessData, setRunningAccountSuccessData] = useState(null);

    const { user } = useAuth();
    const afipEnabled = !!user?.afipEnabled;

    // Determine available invoice types based on feature flags + AFIP org flag
    const availableInvoiceTypes = useMemo(() => {
        const types = [];
        if (afipEnabled && INVOICE_FEATURES.FACTURA_A_ENABLED) types.push({ value: 'A', label: 'Factura A - Responsable Inscripto / Monotributo' });
        if (afipEnabled && INVOICE_FEATURES.FACTURA_B_ENABLED) types.push({ value: 'B', label: 'Factura B - Consumidor Final / Exento' });
        if (afipEnabled && INVOICE_FEATURES.FACTURA_C_ENABLED) types.push({ value: 'C', label: 'Factura C - Consumidor Final' });
        if (INVOICE_FEATURES.PRESUPUESTO_ENABLED) types.push({ value: 'X', label: 'Factura X' });
        return types;
    }, [afipEnabled]);

    // Determine default invoice type (first available type)
    const defaultInvoiceType = useMemo(() => {
        if (availableInvoiceTypes.length === 0) return 'C'; // Fallback
        return availableInvoiceTypes[0].value;
    }, [availableInvoiceTypes]);
    
    // Invoice configuration state
    const [invoiceType, setInvoiceType] = useState(defaultInvoiceType);
    // GIULIANO feature: cuando useCreditCardPrice esta activo en el carrito, forzar 203 (Tarjeta de credito)
    const useCreditCardPrice = !!useContext(CartContext)?.useCreditCardPrice;
    const [paymentMethod, setPaymentMethod] = useState(useCreditCardPrice ? '203' : '201');

    // Split payment state (for "Smart Otra" feature)
    // GIULIANO feature: cuando useCreditCardPrice esta activo, forzar 'single' (split deshabilitado)
    const [paymentMode, setPaymentMode] = useState('single');
    const [payment1, setPayment1] = useState({ method: '201', amount: 0 }); // Default: Efectivo
    const [payment2, setPayment2] = useState({ method: '206', amount: 0 }); // Default: Transferencia
    
    // Client data state (for Factura A/B, and optionally for Presupuesto)
    const [cuitInput, setCuitInput] = useState('');
    const [isSearchingCuit, setIsSearchingCuit] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [showClientModal, setShowClientModal] = useState(false);
    const [clientSearchResult, setClientSearchResult] = useState(null);
    // Informal client name for Presupuesto (Plan 1)
    const [nombreInformal, setNombreInformal] = useState('');

    // No-repeat-invoice guard: shows a confirmation dialog before allowing a duplicate
    const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
    
    // IIBB (Ingresos Brutos) state
    const [iibbData, setIibbData] = useState(null);
    const [isCheckingIIBB, setIsCheckingIIBB] = useState(false);

    // Running Account state (Cuenta Abierta)
    const [runningAccountClients, setRunningAccountClients] = useState([]);
    const [isLoadingRunningAccountClients, setIsLoadingRunningAccountClients] = useState(false);
    const [selectedRunningAccountClient, setSelectedRunningAccountClient] = useState(null);
    
    // Pickup and stock management state
    const [pickedUpBy, setPickedUpBy] = useState('');
    const affectsStock = true; // Always affects stock for running account
    
    // Reset loading state when modal opens/closes
    React.useEffect(() => {
        if (!open) {
            setIsGenerating(false);
            setError('');
            setInvoiceResponse(null);
            setDocumentType('INVOICE'); // Reset to invoice
            setIsBudgetQuoteSuccessOpen(false);
            setBudgetQuoteData(null);
            // Reset split payment state
            setPaymentMode('single');
            setPayment1({ method: '201', amount: 0 });
            setPayment2({ method: '206', amount: 0 });
            // Reset running account state
            setSelectedRunningAccountClient(null);
            setRunningAccountClients([]); // Reset clients so they refresh on next open
            setIsRunningAccountSuccessOpen(false);
            setRunningAccountSuccessData(null);
            // Reset pickup state
            setPickedUpBy('');
            // Reset informal name
            setNombreInformal('');
        }
    }, [open]);

    // Fetch running account clients when document type changes to RUNNING_ACCOUNT
    React.useEffect(() => {
        const fetchRunningAccountClients = async () => {
            if (documentType === 'RUNNING_ACCOUNT') {
                try {
                    setIsLoadingRunningAccountClients(true);
                    const response = await runningAccountService.getClients();
                    const clients = response.clients || response || [];
                    setRunningAccountClients(clients);
                    console.log('[RunningAccount] Clients loaded:', clients.length, 'clients');
                } catch (err) {
                    console.error('[RunningAccount] Error loading clients:', err);
                    setError('Error al cargar clientes de cuenta abierta');
                    setRunningAccountClients([]);
                } finally {
                    setIsLoadingRunningAccountClients(false);
                }
            }
        };
        
        fetchRunningAccountClients();
    }, [documentType]);

    // Format currency
    const formatPrice = useCallback((price) => {
        if (typeof price !== 'number' || isNaN(price)) {
            return 'N/A';
        }
        return `$ ${price.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }, []);

    // Helper function to get effective price (with volume pricing if active)
    const getEffectivePrice = useCallback((item) => {
        const basePrice = typeof getDisplayRetailPrice === 'function' 
            ? getDisplayRetailPrice(item.RetailPrice) 
            : item.RetailPrice;
        
        const isVolumeActive = typeof isVolumePricingActive === 'function' 
            ? isVolumePricingActive(item.productId || item.ProductId) 
            : false;
        
        if (isVolumeActive && typeof getVolumePrice === 'function') {
            return getVolumePrice(basePrice, item.productId || item.ProductId);
        }
        
        return basePrice;
    }, [getDisplayRetailPrice, isVolumePricingActive, getVolumePrice]);

    // Handle CUIT search (supports both CUIT numbers and client name searches)
    const handleSearchCuit = useCallback(async () => {
        const trimmedInput = cuitInput.trim();
        
        if (!trimmedInput) {
            setError('Ingrese un CUIT o nombre de cliente');
            return;
        }

        try {
            setIsSearchingCuit(true);
            setError('');
            
            // Check if input looks like a CUIT (contains only digits and optional dashes)
            const cleanInput = trimmedInput.replace(/[-\s]/g, '');
            const isCuitFormat = /^\d{10,11}$/.test(cleanInput);
            
            if (isCuitFormat) {
                // Search by CUIT with local database + AFIP fallback
                console.log('Searching by CUIT:', cleanInput);
                const result = await searchCuitWithFallback(cuitInput);
                
                console.log('CUIT search result:', result);
                setClientSearchResult(result);
                setShowClientModal(true);
            } else {
                // Search by name in local database
                console.log('Searching by name:', trimmedInput);
                const { searchClientsByName } = await import('../../services/clientService');
                const nameSearchResult = await searchClientsByName(trimmedInput, 10);
                
                if (nameSearchResult && nameSearchResult.results && nameSearchResult.results.length > 0) {
                    console.log('Name search results:', nameSearchResult);
                    
                    if (nameSearchResult.results.length === 1) {
                        // Single result - show it directly
                        const firstMatch = nameSearchResult.results[0];
                        setClientSearchResult({ ...firstMatch, source: 'local' });
                        setShowClientModal(true);
                    } else {
                        // Multiple results - show the first one with a note
                        const firstMatch = nameSearchResult.results[0];
                        setClientSearchResult({ 
                            ...firstMatch, 
                            source: 'local',
                            multipleResults: true,
                            totalResults: nameSearchResult.results.length 
                        });
                        setShowClientModal(true);
                        setError(`Se encontraron ${nameSearchResult.results.length} clientes. Mostrando el primero. Use CUIT para buscar un cliente específico.`);
                    }
                } else {
                    setError(`No se encontraron clientes con el nombre "${trimmedInput}"`);
                }
            }
            
        } catch (err) {
            console.error('Error searching client:', err);
            setError(`Error al buscar cliente: ${err.message}`);
        } finally {
            setIsSearchingCuit(false);
        }
    }, [cuitInput]);

    // Handle client selection without saving
    const handleSelectClientWithoutSave = useCallback(async (clientData) => {
        console.log('Client selected without saving:', clientData);
        setSelectedClient(clientData);
        setShowClientModal(false);
        setError('');
        
        // Check ARBA padrón for IIBB retention/perception
        if (clientData.documento_nro) {
            await checkClientIIBB(clientData.documento_nro);
        }
    }, []);

    // Handle client selection with saving
    const handleSelectClientAndSave = useCallback(async (clientData) => {
        try {
            console.log('Saving client to database:', clientData);
            
            // Save to database
            const savedClient = await saveClientToDatabase(clientData);
            
            console.log('Client saved successfully:', savedClient);
            
            // Update selected client with saved data (includes ID)
            const fullClientData = { ...clientData, ...savedClient, source: 'local' };
            setSelectedClient(fullClientData);
            setShowClientModal(false);
            setError('');
            
            // Check ARBA padrón for IIBB retention/perception
            if (clientData.documento_nro) {
                await checkClientIIBB(clientData.documento_nro);
            }
            
        } catch (err) {
            console.error('Error saving client:', err);
            setError(`Error al guardar cliente: ${err.message}`);
            throw err; // Re-throw to keep modal open
        }
    }, []);

    // Check ARBA padrón for IIBB retention/perception
    const checkClientIIBB = useCallback(async (cuit) => {
        try {
            setIsCheckingIIBB(true);
            console.log('[Invoice] Checking ARBA padrón for IIBB:', cuit);
            
            const iibbResult = await requiresIIBBRetention(cuit);
            console.log('[Invoice] ARBA padrón result:', iibbResult);
            
            setIibbData(iibbResult);
            
            // Show warning if retention/perception applies
            if (iibbResult.requiresRetention || iibbResult.requiresPerception) {
                console.log('[Invoice] Client subject to IIBB retention/perception');
            }
            
        } catch (err) {
            console.error('[Invoice] Error checking ARBA padrón:', err);
            // Don't block invoice generation if ARBA check fails
            setIibbData(null);
        } finally {
            setIsCheckingIIBB(false);
        }
    }, []);

    // Handle invoice type change - reset client data when changing from A/B
    const handleInvoiceTypeChange = useCallback((newType) => {
        setInvoiceType(newType);
        
        // Reset client data if switching away from A/B
        if (newType !== 'A' && newType !== 'B') {
            setSelectedClient(null);
            setCuitInput('');
            setIibbData(null); // Reset IIBB data too
        }
    }, []);

    // Handle clipboard copy for Presupuesto
    const handleCopyToClipboard = useCallback(async () => {
        if (!cartDetails || cartDetails.length === 0) {
            throw new Error('No hay items en el carrito para copiar');
        }

        console.log('[Invoice] Copying cart data to clipboard...');

        // Format cart items (similar to sidebarcart.js)
        const formattedCartDetails = cartDetails.map(item => {
            // Use volume-adjusted price if available
            const effectivePrice = item.RetailPrice; // Already volume-adjusted in cartData
            const descriptionWithSplitter = item.ProductDescription 
                ? `${item.ProductDescription} /${item.splitter || 1}` 
                : `/${item.splitter || 1}`;
            
            return {
                ...item,
                ProductDescription: descriptionWithSplitter,
                RetailPrice: effectivePrice,
                splitter: item.splitter || 1
            };
        });

        // Call Flask clipboard API
        const response = await axios.post(FLASK_CLIPBOARD_API_URL, {
            cartDetails: formattedCartDetails,
            locale: 'es-AR',
            discountPercentage: discountPercentage || 0,
            clientDatabaseName: '' // Not used in invoice context
        });

        const { plainText, htmlText } = response.data;
        console.log('[Invoice] Received clipboard data from Flask');

        // Copy to clipboard using modern API
        if (navigator.clipboard && navigator.clipboard.write) {
            try {
                const textBlob = new Blob([plainText], { type: 'text/plain' });
                const htmlBlob = new Blob([htmlText], { type: 'text/html' });
                const clipboardItem = new ClipboardItem({
                    'text/plain': textBlob,
                    'text/html': htmlBlob,
                });
                await navigator.clipboard.write([clipboardItem]);
                console.log('[Invoice] Data copied to clipboard with format');
            } catch (clipError) {
                console.warn('[Invoice] Clipboard API failed, trying fallback');
                // Fallback: copy plain text only
                await navigator.clipboard.writeText(plainText);
            }
        } else {
            // Legacy fallback
            console.warn('[Invoice] Modern clipboard API not supported');
            await navigator.clipboard.writeText(plainText);
        }
    }, [cartDetails, discountPercentage]);

    // Handle confirm and generate invoice or budget quote
    const handleConfirmGeneration = useCallback(async () => {
        // === DUAL CALCULATION SAFETY CHECK (NEW!) ===
        if (breakdown.isDual && dualCalculation) {
            const safetyCheck = dualCalculation.isSafeToGenerateInvoice();
            
            // Block if not safe
            if (!safetyCheck.safe) {
                setError(safetyCheck.reason || 'Cannot generate invoice at this time');
                console.error('🚫 [Safety Check] Invoice generation blocked:', safetyCheck.reason);
                return;
            }
            
            // Warn if mismatch detected
            if (safetyCheck.warning) {
                const confirmed = window.confirm(
                    `⚠️ Diferencia detectada\n\n${safetyCheck.warning}\n\n¿Desea continuar?`
                );
                if (!confirmed) {
                    console.log('🚫 [Safety Check] User cancelled due to mismatch warning');
                    return;
                }
                console.log('✅ [Safety Check] User accepted mismatch warning');
            }
            
            console.log('✅ [Safety Check] Passed - safe to generate invoice');
        }
        // === END SAFETY CHECK ===
        
        // Validate based on document type
        if (documentType === 'BUDGET_QUOTE') {
            // Creating a budget quote
            try {
                setIsGenerating(true);
                setError('');
                
                // Map condicion_impositiva to cliente_tipo code
                const mapCondicionToTipo = (condicion) => {
                    if (!condicion) return 'CF';
                    const upper = condicion.toUpperCase();
                    if (upper.includes('RESPONSABLE INSCRIPTO') || upper.includes('INSCRIPTO')) return 'RI';
                    if (upper.includes('MONOTRIBUTO')) return 'M';
                    if (upper.includes('EXENTO')) return 'EX';
                    if (upper.includes('CONSUMIDOR FINAL') || upper.includes('FINAL')) return 'CF';
                    return 'CF'; // Default to Consumidor Final
                };
                
                // Fix: apply volume pricing markup to item prices before storing,
                // so each item's precio_unitario matches the volume-adjusted total.
                // Mirrors the same pattern used in the running account flow.
                const cartItemsWithVolumeAdjusted = cartDetails.map(item => {
                    const isVolumeActive = typeof isVolumePricingActive === 'function'
                        ? isVolumePricingActive(item.productId || item.ProductId)
                        : false;

                    let effectiveRetailPrice = item.RetailPrice;
                    if (isVolumeActive && typeof getVolumePrice === 'function' && typeof getDisplayRetailPrice === 'function') {
                        const basePrice = getDisplayRetailPrice(item.RetailPrice);
                        effectiveRetailPrice = getVolumePrice(basePrice, item.productId || item.ProductId, item.quantity);
                    }

                    return {
                        ...item,
                        RetailPrice: effectiveRetailPrice,
                        isVolumePricingActive: isVolumeActive
                    };
                });

                const quoteData = {
                    cartItems: cartItemsWithVolumeAdjusted,
                    subtotal: breakdown.subtotalWithIVA,
                    total: breakdown.finalTotal,
                    discountPercentage: discountPercentage,
                    discountAmount: breakdown.discountAmount,
                    clientData: selectedClient ? {
                        cliente_tipo: selectedClient.cliente_tipo || mapCondicionToTipo(selectedClient.condicion_impositiva),
                        documento_nro: selectedClient.documento_nro || '',
                        razon_social: selectedClient.razon_social || '',
                        nombre_informal: nombreInformal || null,
                        direccion: selectedClient.direccion || '',
                        email: selectedClient.email || '',
                        telefono: selectedClient.telefono || ''
                    } : (nombreInformal ? { nombre_informal: nombreInformal } : null),
                    paymentMethod: paymentMethod,
                    validityDays: 30
                };
                
                console.log('[Invoice] Budget quote data to send:', quoteData);
                
                const response = await budgetQuoteService.createBudgetQuote(quoteData);
                
                console.log('[Invoice] Budget quote created:', response);
                
                // Store quote data with cart totals and open success modal
                // Don't close preview modal yet - it will be closed from success modal handlers
                setBudgetQuoteData({
                    ...response,
                    cartTotals: {
                        subtotal: breakdown.subtotalWithIVA,
                        discount: breakdown.discountAmount,
                        total: breakdown.finalTotal
                    }
                });
                setIsBudgetQuoteSuccessOpen(true);
                setIsGenerating(false);
                
            } catch (error) {
                console.error('[Invoice] Error creating budget quote:', error);
                setError(`Error al generar presupuesto: ${error.response?.data?.message || error.message}`);
                setIsGenerating(false);
            }
        } else if (documentType === 'RUNNING_ACCOUNT') {
            // Adding to running account (cuenta abierta)
            
            console.log('[RunningAccount] Submit - selectedRunningAccountClient:', selectedRunningAccountClient);
            console.log('[RunningAccount] Submit - pickedUpBy value:', pickedUpBy);
            console.log('[RunningAccount] Submit - pickedUpBy type:', typeof pickedUpBy);
            console.log('[RunningAccount] Submit - pickedUpBy length:', pickedUpBy?.length);
            console.log('[RunningAccount] Submit - pickedUpBy trimmed:', pickedUpBy?.trim());
            
            // Validate client selection
            if (!selectedRunningAccountClient) {
                console.error('[RunningAccount] No client selected! State is:', selectedRunningAccountClient);
                setError('Debe seleccionar un cliente de cuenta abierta');
                return;
            }
            
            // Validate required field: Retirado por
            if (!pickedUpBy || pickedUpBy.trim() === '') {
                console.error('[RunningAccount] Validation FAILED - pickedUpBy is empty:', {
                    pickedUpBy,
                    isEmpty: !pickedUpBy,
                    isTrimmedEmpty: pickedUpBy?.trim() === ''
                });
                setError('Debe ingresar el nombre de quien retiró la mercadería');
                return;
            }
            
            console.log('[RunningAccount] ✓ Validation PASSED - pickedUpBy:', pickedUpBy);

            try {
                setIsGenerating(true);
                setError('');
                
                // Prepare cart items with volume pricing state
                const cartItemsWithVolumeState = cartDetails.map(item => {
                    const volumeState = typeof isVolumePricingActive === 'function' 
                        ? isVolumePricingActive(item.productId || item.ProductId)
                        : false;
                    
                    return {
                        ...item,
                        isVolumePricingActive: volumeState
                    };
                });
                
                const runningAccountData = {
                    cartItems: cartItemsWithVolumeState,
                    discountPercentage: discountPercentage || 0,
                    clientData: {
                        id: selectedRunningAccountClient.id, // Running account client ID (primary)
                        documento_nro: selectedRunningAccountClient.document_number || selectedRunningAccountClient.documento_nro || selectedRunningAccountClient.cuit || '',
                        razon_social: selectedRunningAccountClient.business_name || selectedRunningAccountClient.razon_social || selectedRunningAccountClient.nombre || '',
                        direccion: selectedRunningAccountClient.address || selectedRunningAccountClient.direccion || '',
                        condicion_impositiva: selectedRunningAccountClient.tax_condition || selectedRunningAccountClient.condicion_impositiva || '',
                        condicion_iva: selectedRunningAccountClient.tax_condition_code || selectedRunningAccountClient.condicion_iva || 'CF'
                    },
                    // Stock management - always affects stock
                    pickedUp: true,                          // Always true when adding to running account
                    pickedUpBy: pickedUpBy.trim(),
                    affectsStock: true                       // Always affects stock for running account
                };
                
                console.log('[RunningAccount] Data to send:', runningAccountData);
                
                // Pass volume pricing functions
                const volumePricingFunctions = {
                    getVolumePrice,
                    getDisplayRetailPrice,
                    isVolumePricingActive
                };
                
                const response = await runningAccountService.addToRunningAccount(runningAccountData, volumePricingFunctions);
                
                console.log('[RunningAccount] Successfully added to account:', response);
                
                // Store success data and show success modal
                setRunningAccountSuccessData({
                    clientData: selectedRunningAccountClient,
                    movementData: response.data,
                    itemCount: cartItemsWithVolumeState.length,
                    total: breakdown.finalTotal || breakdown.totalWithIVA || breakdown.total
                });
                setIsRunningAccountSuccessOpen(true);
                setIsGenerating(false);
                // Don't clear cart here - let user choose in success modal
                
            } catch (error) {
                console.error('[RunningAccount] Error adding to running account:', error);
                setError(`Error al agregar a cuenta abierta: ${error.response?.data?.message || error.message}`);
            } finally {
                setIsGenerating(false);
            }
        } else {
            // Creating an invoice (existing flow)
            
            // Validate client data for Factura A (required)
            if (invoiceType === 'A' && !selectedClient) {
                setError('Debe buscar y seleccionar un cliente para Factura A');
                return;
            }

            // Validate split payment if mode is 'split'
            if (paymentMode === 'split') {
                const sum = payment1.amount + payment2.amount;
                const diff = Math.abs(sum - breakdown.finalTotal);
                
                if (diff > 0.01) {
                    setError(`La suma de los pagos ($${sum.toFixed(2)}) debe ser igual al total ($${breakdown.finalTotal.toFixed(2)})`);
                    return;
                }
                if (payment1.method === payment2.method) {
                    setError('Los métodos de pago deben ser diferentes');
                    return;
                }
                if (payment1.amount <= 0 || payment2.amount <= 0) {
                    setError('Ambos montos de pago deben ser mayores a $0');
                    return;
                }
            }

            // For Factura B, client data is optional (only required over $10M)
            // For Presupuesto (X), client data is optional
            // For Factura C, no client data needed (Consumidor Final)

            try {
                setIsGenerating(true);
                setError('');
                setInvoiceResponse(null);
                
                // If Presupuesto and clipboard copy enabled, copy data first
                if (invoiceType === 'X' && INVOICE_FEATURES.PRESUPUESTO_COPY_TO_CLIPBOARD) {
                    try {
                        await handleCopyToClipboard();
                        console.log('[Invoice] Clipboard copy successful for Presupuesto');
                    } catch (copyError) {
                        console.error('[Invoice] Clipboard copy failed, continuing with generation:', copyError);
                        // Don't block invoice generation if clipboard fails
                    }
                }
                
                // Prepare split payment data if applicable
                const splitPaymentData = paymentMode === 'split' ? {
                    paymentMode: 'split',
                    splitPayment: {
                        payment1: {
                            method: payment1.method,
                            name: getPaymentMethodName(payment1.method),
                            amount: payment1.amount
                        },
                        payment2: {
                            method: payment2.method,
                            name: getPaymentMethodName(payment2.method),
                            amount: payment2.amount
                        }
                    }
                } : {
                    paymentMode: 'single'
                };
                
                // Pass invoice type, payment method, client data, and split payment data to parent handler
                // For split payments, paymentMethod will be '208' (Otra) to AFIP
                const effectivePaymentMethod = paymentMode === 'split' ? '208' : paymentMethod;
                
                // Determine if this is a BUDGET_QUOTE (Presupuesto - No Registra Venta)
                // Note: Factura X is NOT a budget quote - it's a sale that affects stock
                const isBudgetQuote = documentType === 'BUDGET_QUOTE';
                
                // Determine if this should register with AFIP
                // Factura X may or may not register with AFIP, but it's still a sale
                const registerWithAFIP = documentType !== 'BUDGET_QUOTE';
                
                // Add both flags to splitPaymentData
                const extendedPaymentData = {
                    ...splitPaymentData,
                    isBudgetQuote,      // For stock control
                    registerWithAFIP    // For AFIP registration
                };
                
                // Use V2 endpoint if enabled, otherwise use V1
                // Always use V2 endpoint
                console.log('🔵 [Invoice Modal] Using V2 create endpoint');
                
                // Determine client tax condition based on invoice type and client data
                let clientTaxCondition = 'CF'; // Default to Consumidor Final, IVA 21%
                
                if (invoiceType === 'B') {
                    // Factura B is ALWAYS Consumidor Final (CF) with 21% IVA
                    clientTaxCondition = 'CF';
                    console.log('📋 [Invoice Modal] Factura B - Using CF (Consumidor Final) with 21% IVA');
                } else if (invoiceType === 'A') {
                    // Factura A requires client with valid tax condition
                    if (selectedClient) {
                        const clientCondition = selectedClient.condicion_impositiva || selectedClient.cliente_tipo;
                        
                        // Map client condition to valid codes
                        if (clientCondition === 'RESPONSABLE INSCRIPTO' || clientCondition === 'RI') {
                            clientTaxCondition = 'RI';
                        } else if (clientCondition === 'MONOTRIBUTO' || clientCondition === 'M') {
                            clientTaxCondition = 'M';
                        } else if (clientCondition === 'EXENTO' || clientCondition === 'EX') {
                            // Exento only valid if CUIT is validated
                            if (selectedClient.documento_nro && selectedClient.documento_nro.length > 0) {
                                clientTaxCondition = 'EX';
                                console.log('📋 [Invoice Modal] Factura A - Using EX (Exento) with validated CUIT');
                            } else {
                                // No CUIT, default to CF
                                clientTaxCondition = 'CF';
                                console.warn('⚠️ [Invoice Modal] Exento without CUIT - Defaulting to CF');
                            }
                        } else {
                            // Unknown condition, default to CF
                            clientTaxCondition = 'CF';
                            console.warn('⚠️ [Invoice Modal] Unknown tax condition - Defaulting to CF');
                        }
                    } else {
                        // Factura A without client should not happen (validated earlier), but default to CF
                        clientTaxCondition = 'CF';
                    }
                } else {
                    // Factura C, X (Presupuesto), or other types - always CF
                    clientTaxCondition = 'CF';
                }
                
                console.log('📋 [Invoice Modal] Final clientTaxCondition:', clientTaxCondition);
                
                // CRITICAL: Add volume pricing state to cart items before sending
                console.log('⚡ [Invoice Modal] Current cart details:', cartDetails.map(item => ({
                    productId: item.productId,
                    ProductId: item.ProductId,
                    ProductCode: item.ProductCode
                })));
                
                const cartItemsWithVolumeState = cartDetails.map(item => {
                    const volumeState = typeof isVolumePricingActive === 'function' 
                        ? isVolumePricingActive(item.productId)
                        : false;
                    
                    console.log(`⚡ [Invoice Modal] Product ${item.ProductCode} (ID: ${item.productId}): volumeState = ${volumeState}`);
                    
                    return {
                        ...item,
                        isVolumePricingActive: volumeState
                    };
                });
                
                console.log('⚡ [Invoice Modal] Cart items with volume pricing:', cartItemsWithVolumeState.map(item => ({
                    productId: item.productId,
                    ProductCode: item.ProductCode,
                    isVolumePricingActive: item.isVolumePricingActive
                })));
                
                const v2Payload = {
                    cartItems: cartItemsWithVolumeState,
                    discountPercentage: discountPercentage,
                    invoiceType: invoiceType,
                    paymentMethod: effectivePaymentMethod,
                    clientData: selectedClient,
                    clientTaxCondition: clientTaxCondition, // Always provide a valid tax condition
                    registerWithAFIP: registerWithAFIP,
                    isBudgetQuote: isBudgetQuote,
                    paymentMode: paymentMode,
                    splitPayment: paymentMode === 'split' ? splitPaymentData.splitPayment : null
                };
                
                console.log('📦 [Invoice Modal] V2 Payload:', v2Payload);
                
                // Pass volume pricing functions to createInvoiceV2 so it can calculate volume prices
                const volumePricingFunctions = {
                    getVolumePrice,
                    getDisplayRetailPrice,
                    isVolumePricingActive
                };
                
                const result = await createInvoiceV2(v2Payload, volumePricingFunctions);
                
                console.log('✅ [Invoice Modal] V2 Invoice created:', result);
                console.log('📋 [Invoice Modal] Response structure:', {
                    hasSuccess: !!result.success,
                    hasInvoice: !!result.invoice,
                    hasCae: !!result.invoice?.cae,
                    invoiceNumber: result.invoice?.comprobante_nro,
                    version: result.version
                });
                console.log('💰 [Invoice Modal] Backend calculation:', result.invoice?.calculation);
                console.log('💰 [Invoice Modal] Local breakdown:', breakdown);
                
                // Handle queued response (AFIP temporarily unavailable)
                if (result.queued) {
                    console.log('⏳ [Invoice Modal] Invoice queued:', result);
                    const queuedInvoiceData = {
                        queued: true,
                        // Core identification
                        invoiceId: result.invoice.invoice_id,
                        queueId: result.invoice.queueId,
                        external_reference: result.invoice.external_reference,
                        externalReference: result.invoice.external_reference,
                        // Status
                        status: 'EN_COLA',
                        afip_status: 'EN_COLA',
                        invoiceType: invoiceType,
                        tipo_comprobante: invoiceType,
                        // No CAE yet
                        cae: null,
                        cae_vencimiento: null,
                        // Dates
                        fecha_emision: new Date().toISOString(),
                        fechaEmision: new Date().toISOString(),
                        // Financial details from calculation
                        total: parseFloat(result.invoice.calculation?.total || breakdown.finalTotal || 0),
                        subtotal: parseFloat(result.invoice.calculation?.subtotalSinIva || breakdown.subtotalAfterDiscount || 0),
                        subtotal_sin_iva: parseFloat(result.invoice.calculation?.subtotalSinIva || breakdown.subtotalAfterDiscount || 0),
                        iva: parseFloat(result.invoice.calculation?.iva || breakdown.totalIVA || 0),
                        descuento_porcentaje: discountPercentage || 0,
                        descuento_importe: parseFloat(result.invoice.calculation?.discount || breakdown.discountAmount || 0),
                        discount: parseFloat(result.invoice.calculation?.discount || breakdown.discountAmount || 0),
                        // Client data
                        clientData: selectedClient,
                        paymentMethod: effectivePaymentMethod,
                        payment_method: effectivePaymentMethod
                    };
                    if (onConfirmGeneration) onConfirmGeneration(queuedInvoiceData);
                    onClose();

                // Handle successful (non-queued) response
                } else if (result.success && result.invoice) {
                    // Prepare invoice data for success modal
                    // Map V2 response to format expected by InvoiceSuccessModal
                    
                    // Extract invoice number - backend may return it in different formats
                    const invoiceNumber = result.invoice.comprobante_nro || 
                                        result.invoice.numeroComprobante || 
                                        result.invoice.numero_comprobante ||
                                        `00005-${String(result.invoice.invoice_id).padStart(8, '0')}`;
                    
                    const invoiceData = {
                        // Core identification
                        invoiceId: result.invoice.invoice_id,
                        external_reference: result.invoice.external_reference,
                        externalReference: result.invoice.external_reference,
                        
                        // Invoice number fields (multiple formats for compatibility)
                        numero_comprobante: invoiceNumber,
                        numeroComprobante: invoiceNumber,
                        invoiceNumber: invoiceNumber,
                        numero: result.invoice.invoice_id,
                        
                        // Invoice type
                        tipo_comprobante: result.invoice.comprobante_tipo || invoiceType,
                        invoiceType: invoiceType,
                        status: isBudgetQuote ? 'PRESUPUESTO' : 'APROBADA',
                        afip_status: 'APROBADA',
                        
                        // AFIP data
                        cae: result.invoice.cae?.trim() || result.cae,
                        cae_vencimiento: result.invoice.vencimiento_cae || result.caeVencimiento,
                        caeVencimiento: result.invoice.vencimiento_cae || result.caeVencimiento,
                        vencimientoCae: result.invoice.vencimiento_cae || result.caeVencimiento,
                        
                        // Dates
                        fecha_emision: new Date().toISOString(),
                        fechaEmision: new Date().toISOString(),
                        fecha_vencimiento: result.invoice.vencimiento_pago,
                        fechaVencimiento: result.invoice.vencimiento_pago,
                        vencimientoPago: result.invoice.vencimiento_pago,
                        
                        // Punto de venta
                        punto_venta: '00005',
                        puntoVenta: '00005',
                        
                        // Financial details (CRITICAL: Use local breakdown if backend calculation missing)
                        // Backend may not return calculation, so fallback to local breakdown
                        // IMPORTANT: subtotal should be AFTER discount, not before!
                        // For queued invoices, total is in calculation.total
                        total: parseFloat(result.invoice.calculation?.total || result.invoice.total || breakdown.totalWithIVA || breakdown.total),
                        subtotal: parseFloat(result.invoice.calculation?.subtotalSinIva || breakdown.subtotalAfterDiscount || breakdown.subtotalWithoutIVA),
                        subtotal_sin_iva: parseFloat(result.invoice.calculation?.subtotalSinIva || breakdown.subtotalAfterDiscount || breakdown.subtotalWithoutIVA),
                        iva: parseFloat(result.invoice.calculation?.iva || breakdown.totalIVA || breakdown.iva),
                        
                        // Discount fields (InvoiceSuccessModal looks for these)
                        descuento_porcentaje: discountPercentage || 0,
                        descuento_importe: parseFloat(result.invoice.calculation?.discount || breakdown.discountAmount || 0),
                        discount: parseFloat(result.invoice.calculation?.discount || breakdown.discountAmount || 0),
                        
                        // URLs
                        pdfUrl: result.invoice.comprobante_pdf_url || result.pdfUrl,
                        pdf_url: result.invoice.comprobante_pdf_url || result.pdfUrl,
                        ticketUrl: result.invoice.comprobante_ticket_url || result.ticketUrl,
                        ticket_url: result.invoice.comprobante_ticket_url || result.ticketUrl,
                        qrUrl: result.invoice.afip_qr || result.qrUrl,
                        qr_url: result.invoice.afip_qr || result.qrUrl,
                        
                        // Client data
                        clientData: selectedClient,
                        
                        // Payment method
                        paymentMethod: effectivePaymentMethod,
                        payment_method: effectivePaymentMethod
                    };
                    
                    console.log('✅ [Invoice Modal] Invoice data prepared for parent:', invoiceData);
                    console.log('✅ [Invoice Modal] Financial values:', {
                        total: invoiceData.total,
                        subtotal: invoiceData.subtotal,
                        discount: invoiceData.descuento_importe,
                        discountPercent: invoiceData.descuento_porcentaje,
                        iva: invoiceData.iva,
                        invoiceNumber: invoiceData.invoiceNumber
                    });
                    
                    // Close immediately — success feedback is shown in InvoiceSuccessModal
                    if (onConfirmGeneration) onConfirmGeneration(invoiceData);
                    onClose();

                } else {
                    // API returned success:false — keep modal open, userMsg will render the error
                    console.error('❌ [Invoice Modal] API returned error response:', result);
                }
            } catch (err) {
                console.error('❌ [Invoice Modal] Error generating invoice:', err);
                console.error('❌ [Invoice Modal] Error details:', {
                    message: err.message,
                    response: err.response?.data
                });
                
                // Helper central: traduce el error de ARCA al shape normalizado
                // (success/info/warning/error) con title/mensaje/accion.
                if (err.response?.data) {
                    setInvoiceResponse(err.response.data);
                } else {
                    const t = mapErrorToAlert(err);
                    setError(t.mensaje || t.title || 'Error desconocido');
                }
            } finally {
                setIsGenerating(false);
            }
        }
    }, [documentType, createInvoiceV2, invoiceType, paymentMethod, paymentMode, payment1, payment2, selectedClient, nombreInformal, cartDetails, discountPercentage, breakdown, onClose, handleCopyToClipboard, selectedRunningAccountClient, pickedUpBy, isVolumePricingActive, getVolumePrice, onConfirmGeneration, dualCalculation]);

    // ── No-repeat-invoice guard ───────────────────────────────────────────────────────────────────────
    // Intercepts the submit button click when the current cart matches the last
    // invoiced snapshot, surfacing a confirmation before proceeding.
    const handleGenerateClick = useCallback(() => {
        if (isAlreadyInvoiced) {
            setShowDuplicateConfirm(true);
        } else {
            handleConfirmGeneration();
        }
    }, [isAlreadyInvoiced, handleConfirmGeneration]);
    // ───────────────────────────────────────────────────────────────────────────────

    // Use breakdown for accurate discount display
    const discountAmount = breakdown.discountAmount;

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        maxHeight: '95vh',
                        minHeight: '600px',
                        display: 'flex',
                        flexDirection: 'column',
                        // Hide the dialog when success modal is shown, but keep it mounted
                        visibility: (isBudgetQuoteSuccessOpen || isRunningAccountSuccessOpen) ? 'hidden' : 'visible'
                    }
                }}
            >
            <DialogTitle sx={{ 
                backgroundColor: '#4775ba', 
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ReceiptIcon fontSize="large" />
                    <Typography variant="h5" component="div">
                        Vista Previa de Factura
                    </Typography>
                </Box>
                <IconButton
                    edge="end"
                    color="inherit"
                    onClick={onClose}
                    aria-label="close"
                    disabled={isGenerating}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3, flex: 1, overflow: 'auto' }}>
                {(() => {
                    const userMsg = getInvoiceUserMessage(invoiceResponse);
                    // Only show error/warning alerts — success/queued are handled by InvoiceSuccessModal
                    if (userMsg && (userMsg.type === 'error' || userMsg.type === 'warning')) {
                        return (
                            <Alert severity={userMsg.color} sx={{ mb: 2, whiteSpace: 'pre-line' }}>
                                {userMsg.message}
                            </Alert>
                        );
                    }
                    return null;
                })()}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                {/* V2 Calculation Indicator */}
                {breakdown.calculationSource === 'backend-v2' && (
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                        <Chip 
                            label="✅ Cálculos del servidor" 
                            color="success" 
                            size="small"
                            icon={breakdown.isCalculating ? <CircularProgress size={12} color="inherit" /> : undefined}
                        />
                        {breakdown.isCalculating && (
                            <Typography variant="caption" color="text.secondary">
                                Actualizando cálculos...
                            </Typography>
                        )}
                    </Box>
                )}
                {breakdown.error && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            ⚠️ Usando cálculos locales. {breakdown.error}
                        </Typography>
                    </Alert>
                )}

                <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                        Revise los detalles de la factura antes de enviarla a AFIP. 
                        Los precios mostrados son los que aparecerán en el comprobante oficial.
                    </Typography>
                </Alert>

                {/* Invoice Configuration Section */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom color="primary">
                        Configuración de Comprobante
                    </Typography>
                    
                    {/* Document Type Selection - Invoice, Budget Quote, or Running Account */}
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel id="document-type-label">Tipo de Operación</InputLabel>
                        <Select
                            labelId="document-type-label"
                            id="document-type"
                            value={documentType}
                            label="Tipo de Operación"
                            onChange={(e) => {
                                setDocumentType(e.target.value);
                                // Reset client selections when changing document type
                                if (e.target.value === 'RUNNING_ACCOUNT') {
                                    setSelectedClient(null);
                                    setCuitInput('');
                                    setPickedUpBy(''); // Reset pickup field
                                } else {
                                    setSelectedRunningAccountClient(null);
                                    setPickedUpBy(''); // Reset pickup field
                                }
                            }}
                            disabled={isGenerating}
                        >
                            <MenuItem value="INVOICE">Factura ó Proforma (Venta)</MenuItem>
                            <MenuItem value="BUDGET_QUOTE">Presupuesto (No Registra Venta)</MenuItem>
                            <MenuItem value="RUNNING_ACCOUNT">Agregar a Cuenta Abierta</MenuItem>
                        </Select>
                    </FormControl>

                    {documentType === 'BUDGET_QUOTE' && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                <strong>Presupuesto:</strong> Este documento NO se registrará como venta 
                                en AFIP y NO afectará las estadísticas. Podrá convertirlo a factura desde la 
                                página de Presupuestos.
                            </Typography>
                        </Alert>
                    )}

                    {documentType === 'RUNNING_ACCOUNT' && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                <strong>Cuenta Abierta:</strong> Los items se agregarán a la cuenta abierta del cliente seleccionado. 
                                NO se generará factura ni se registrará en AFIP desde este paso.
                            </Typography>
                        </Alert>
                    )}

                    {/* Running Account Client Selector - Only for RUNNING_ACCOUNT */}
                    {documentType === 'RUNNING_ACCOUNT' && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                                Seleccionar Cliente de Cuenta Abierta
                            </Typography>
                            
                            <FormControl fullWidth>
                                <InputLabel id="running-account-client-label">Cliente</InputLabel>
                                <Select
                                    labelId="running-account-client-label"
                                    id="running-account-client"
                                    value={selectedRunningAccountClient ? String(selectedRunningAccountClient.id || selectedRunningAccountClient.documento_nro || '') : ''}
                                    label="Cliente"
                                    onChange={(e) => {
                                        const selectedValue = e.target.value;
                                        const client = runningAccountClients.find(
                                            c => String(c.id || c.documento_nro) === selectedValue
                                        );
                                        console.log('[RunningAccount] Selected client:', client, 'from value:', selectedValue);
                                        setSelectedRunningAccountClient(client || null);
                                    }}
                                    disabled={isGenerating || isLoadingRunningAccountClients}
                                >
                                    {isLoadingRunningAccountClients ? (
                                        <MenuItem disabled>
                                            <CircularProgress size={20} sx={{ mr: 1 }} />
                                            Cargando clientes...
                                        </MenuItem>
                                    ) : runningAccountClients.length === 0 ? (
                                        <MenuItem disabled>No hay clientes con cuenta abierta</MenuItem>
                                    ) : (
                                        runningAccountClients.map((client) => (
                                            <MenuItem 
                                                key={client.id || client.documento_nro || client.cuit} 
                                                value={String(client.id || client.documento_nro)}
                                            >
                                                {client.business_name || client.razon_social || client.nombre} - {client.document_number || client.documento_nro || client.cuit}
                                            </MenuItem>
                                        ))
                                    )}
                                </Select>
                            </FormControl>

                            {/* Display selected client details */}
                            {selectedRunningAccountClient && (
                                <Box sx={{ 
                                    mt: 2,
                                    p: 2, 
                                    backgroundColor: '#fff3e0', 
                                    borderRadius: 1,
                                    border: '1px solid #ff9800'
                                }}>
                                    <Typography variant="subtitle2" color="warning.main" gutterBottom>
                                        Datos del Cliente
                                    </Typography>
                                    <Typography variant="body1" fontWeight="bold">
                                        {selectedRunningAccountClient.business_name || selectedRunningAccountClient.razon_social || selectedRunningAccountClient.nombre}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        CUIT/DNI: {selectedRunningAccountClient.document_number || selectedRunningAccountClient.documento_nro || selectedRunningAccountClient.cuit}
                                    </Typography>
                                    {(selectedRunningAccountClient.tax_condition || selectedRunningAccountClient.condicion_impositiva) && (
                                        <Typography variant="body2" color="text.secondary">
                                            Condición: {selectedRunningAccountClient.tax_condition || selectedRunningAccountClient.condicion_impositiva}
                                        </Typography>
                                    )}
                                    {(selectedRunningAccountClient.address || selectedRunningAccountClient.direccion) && (
                                        <Typography variant="body2" color="text.secondary">
                                            Dirección: {selectedRunningAccountClient.address || selectedRunningAccountClient.direccion}
                                        </Typography>
                                    )}
                                    {(selectedRunningAccountClient.current_balance !== undefined || selectedRunningAccountClient.saldo_pendiente !== undefined) && (
                                        <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold', color: (selectedRunningAccountClient.current_balance || selectedRunningAccountClient.saldo_pendiente || 0) > 0 ? '#d32f2f' : '#2e7d32' }}>
                                            Saldo Pendiente: ${(selectedRunningAccountClient.current_balance || selectedRunningAccountClient.saldo_pendiente || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                                        </Typography>
                                    )}
                                </Box>
                            )}
            
            {/* Stock Management - Only show after client selection */}
            {selectedRunningAccountClient && (
                <Box sx={{ 
                    mt: 3,
                    p: 2, 
                    backgroundColor: '#e8f5e9', 
                    borderRadius: 1,
                    border: '1px solid #4caf50'
                }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold" color="success.main">
                        Gestión de Mercadería
                    </Typography>
                    
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            ✓ El stock se descontará automáticamente del inventario al agregar a cuenta abierta.
                        </Typography>
                    </Alert>
                    
                    <TextField
                        fullWidth
                        required
                        label="Retirado por"
                        placeholder="Nombre de quien retiró la mercadería"
                        value={pickedUpBy}
                        onChange={(e) => {
                            console.log('[RunningAccount] pickedUpBy field changed:', e.target.value);
                            setPickedUpBy(e.target.value);
                        }}
                        disabled={isGenerating}
                        error={!pickedUpBy && documentType === 'RUNNING_ACCOUNT'}
                        helperText={!pickedUpBy ? "Campo obligatorio" : ""}
                        size="small"
                    />
                </Box>
            )}
        </Box>
    )}
    
    {/* Invoice Type and Payment Method - Only show for INVOICE and BUDGET_QUOTE */}
    {documentType !== 'RUNNING_ACCOUNT' && (
        <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2
        }}>
            {/* Invoice Type Selector - Only show for INVOICE */}
            {documentType === 'INVOICE' && (
                <FormControl fullWidth>
                    <InputLabel id="invoice-type-label">Tipo de Comprobante</InputLabel>
                    <Select
                        labelId="invoice-type-label"
                        id="invoice-type"
                        value={invoiceType}
                        label="Tipo de Comprobante"
                        onChange={(e) => handleInvoiceTypeChange(e.target.value)}
                        disabled={isGenerating}
                    >
                        {availableInvoiceTypes.map((type) => (
                            <MenuItem key={type.value} value={type.value}>
                                {type.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            {/* Payment Method Selector - Single Mode Only, not for RUNNING_ACCOUNT */}
            {paymentMode === 'single' && (
                <FormControl fullWidth>
                    <InputLabel id="payment-method-label">Método de Pago</InputLabel>
                    <Select
                        labelId="payment-method-label"
                        id="payment-method"
                        value={paymentMethod}
                        label="Método de Pago"
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        disabled={isGenerating || useCreditCardPrice}
                    >
                        <MenuItem value="201">Contado</MenuItem>
                        <MenuItem value="202">Tarjeta de débito</MenuItem>
                        <MenuItem value="203">Tarjeta de crédito</MenuItem>
                        <MenuItem value="204">Cuenta corriente</MenuItem>
                        <MenuItem value="205">Cheque</MenuItem>
                        <MenuItem value="206">Transferencia Bancaria</MenuItem>
                        <MenuItem value="207">Otros medios de pago electrónico</MenuItem>
                        <MenuItem value="208">Otra</MenuItem>
                    </Select>
                </FormControl>
            )}
        </Box>
    )}

    {/* Payment Mode Selection - Single vs Split - Only for INVOICE and BUDGET_QUOTE */}
    {documentType !== 'RUNNING_ACCOUNT' && useCreditCardPrice && (
        <Box sx={{ mt: 3, mb: 2, p: 2, backgroundColor: '#e6f4ff', border: '1px solid #91caff', borderRadius: '6px' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#003a8c' }}>
                Modo Precio Crédito activo — pago con Tarjeta de Crédito (203)
            </Typography>
        </Box>
    )}
    {documentType !== 'RUNNING_ACCOUNT' && (
        <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Configuración de Pago
            </Typography>
            
            <FormControl component="fieldset">
                <RadioGroup
                    row
                    value={paymentMode}
                    onChange={(e) => {
                        setPaymentMode(e.target.value);
                        // Reset amounts when switching modes
                        if (e.target.value === 'split') {
                            setPayment1({ method: '201', amount: 0 });
                            setPayment2({ method: '206', amount: 0 });
                        }
                    }}
                >
                    <FormControlLabel
                        value="single"
                        control={<Radio />}
                        label="Un método de pago"
                        disabled={isGenerating}
                    />
                    <FormControlLabel
                        value="split"
                        control={<Radio />}
                        label="Dos métodos de pago (dividir)"
                        disabled={isGenerating || useCreditCardPrice}
                    />
                </RadioGroup>
            </FormControl>

            {/* Split Payment Selector */}
            {paymentMode === 'split' && (
                <Box sx={{ mt: 2 }}>
                    <SplitPaymentSelector
                        total={breakdown.finalTotal}
                        payment1={payment1}
                        payment2={payment2}
                        onPayment1Change={setPayment1}
                        onPayment2Change={setPayment2}
                        disabled={isGenerating}
                    />
                </Box>
            )}
        </Box>
    )}

    {/* CUIT Input Section - For Factura A (required) and optionally for Presupuesto (X) or Factura B */}
    {/* Not shown for RUNNING_ACCOUNT since client is selected from dropdown */}
    {documentType !== 'RUNNING_ACCOUNT' && ((documentType === 'INVOICE' && invoiceType === 'A') || 
      (documentType === 'INVOICE' && invoiceType === 'B' && INVOICE_FEATURES.FACTURA_B_ALLOW_CLIENT_SEARCH) ||
      (documentType === 'INVOICE' && invoiceType === 'X' && INVOICE_FEATURES.PRESUPUESTO_ALLOW_CLIENT_SEARCH) ||
      (documentType === 'BUDGET_QUOTE')) && (
        <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                {(documentType === 'INVOICE' && invoiceType === 'A') 
                    ? 'Datos del Cliente' 
                    : 'Datos del Cliente (Opcional)'}
            </Typography>
            
            {/* Informal name field — only for BUDGET_QUOTE */}
            {documentType === 'BUDGET_QUOTE' && (
                <TextField
                    fullWidth
                    label="Nombre / Apodo"
                    placeholder="Ej: Juan el del taller"
                    value={nombreInformal}
                    onChange={(e) => setNombreInformal(e.target.value)}
                    disabled={isGenerating}
                    helperText="Nombre informal del cliente para el presupuesto (opcional, máx. 255 caracteres)"
                    inputProps={{ maxLength: 255 }}
                    sx={{ mb: 2 }}
                />
            )}

            {(documentType === 'BUDGET_QUOTE' || (documentType === 'INVOICE' && (invoiceType === 'X' || invoiceType === 'B'))) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        {documentType === 'BUDGET_QUOTE' && (
                            <>Para el Presupuesto, los datos del cliente son <strong>opcionales</strong>. 
                            Puede buscar y guardar el CUIT para su registro interno.</>
                        )}
                        {documentType === 'INVOICE' && invoiceType === 'X' && (
                            <>Para la Factura X, los datos del cliente son <strong>opcionales</strong>. 
                            Puede buscar y guardar el CUIT para su registro interno sin registrar en AFIP.</>
                        )}
                        {documentType === 'INVOICE' && invoiceType === 'B' && (
                            <>Los datos del cliente son <strong>opcionales para Factura B</strong>. 
                            Solo son obligatorios si el monto supera los <strong>$10.000.000 ARS</strong>. 
                            </>
                        )}
                    </Typography>
                </Alert>
            )}
            
            {!selectedClient ? (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField
                        fullWidth
                        label="CUIT o Razón Social"
                        placeholder="20-12345678-9 o Razón Social"
                        value={cuitInput}
                        onChange={(e) => setCuitInput(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && cuitInput && !isSearchingCuit && !isGenerating) {
                                                handleSearchCuit();
                                            }
                                        }}
                                        disabled={isSearchingCuit || isGenerating}
                                        helperText="Ingrese el CUIT (11 dígitos) o razón social del cliente"
                                        inputProps={{ maxLength: 50 }}
                                    />
                                    <Button
                                        variant="contained"
                                        startIcon={isSearchingCuit ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                                        onClick={handleSearchCuit}
                                        disabled={isSearchingCuit || isGenerating || !cuitInput}
                                        sx={{ 
                                            minWidth: '120px',
                                            height: '56px'
                                        }}
                                    >
                                        {isSearchingCuit ? 'Buscando...' : 'Buscar'}
                                    </Button>
                                </Box>
                            ) : (
                                <Box sx={{ 
                                    p: 2, 
                                    backgroundColor: '#e8f5e9', 
                                    borderRadius: 1,
                                    border: '1px solid #4caf50'
                                }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body1" fontWeight="bold">
                                                {selectedClient.razon_social}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                CUIT: {selectedClient.documento_nro}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Condición: {selectedClient.condicion_impositiva}
                                            </Typography>
                                            {selectedClient.direccion && (
                                                <Typography variant="body2" color="text.secondary">
                                                    Dirección: {selectedClient.direccion}
                                                </Typography>
                                            )}
                                            
                                            {/* IIBB Status Display */}
                                            {isCheckingIIBB && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                                    <CircularProgress size={16} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        Consultando padrones ARBA y AGIP...
                                                    </Typography>
                                                </Box>
                                            )}
                                            
                                            {!isCheckingIIBB && iibbData && (() => {
                                                const status = getDetailedIIBBStatus(iibbData, 'sale');
                                                const hasRetentionOrPerception = iibbData.requiresRetention || iibbData.requiresPerception;
                                                
                                                return (
                                                    <Box sx={{ 
                                                        mt: 1, 
                                                        p: 1.5, 
                                                        backgroundColor: hasRetentionOrPerception ? '#fff3e0' : '#f5f5f5',
                                                        borderRadius: 1,
                                                        border: hasRetentionOrPerception ? '1px solid #ff9800' : '1px solid #e0e0e0'
                                                    }}>
                                                        <Typography 
                                                            variant="caption" 
                                                            fontWeight="bold"
                                                            color={hasRetentionOrPerception ? 'warning.main' : 'text.secondary'}
                                                        >
                                                            📋 Ingresos Brutos
                                                        </Typography>
                                                        <Typography variant="body2" fontSize="0.85rem" fontWeight="medium">
                                                            {status.primary}
                                                        </Typography>
                                                        
                                                        {status.details.length > 0 && (
                                                            <Box sx={{ mt: 0.5, pl: 1 }}>
                                                                {status.details.map((detail, index) => (
                                                                    <Typography 
                                                                        key={index} 
                                                                        variant="caption" 
                                                                        display="block" 
                                                                        color="text.secondary"
                                                                        sx={{ fontSize: '0.75rem' }}
                                                                    >
                                                                        • {detail}
                                                                    </Typography>
                                                                ))}
                                                            </Box>
                                                        )}
                                                        
                                                        {hasRetentionOrPerception && (
                                                            <Typography variant="caption" color="warning.dark" display="block" sx={{ mt: 0.5, fontWeight: 'bold' }}>
                                                                ⚠️ Se aplicará automáticamente en la factura
                                                            </Typography>
                                                        )}
                                                        
                                                        {status.warning && !hasRetentionOrPerception && (
                                                            <Typography variant="caption" color="info.main" display="block" sx={{ mt: 0.5 }}>
                                                                ℹ️ {status.warning}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                );
                                            })()}
                                        </Box>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => {
                                                setSelectedClient(null);
                                                setCuitInput('');
                                                setIibbData(null);
                                            }}
                                            disabled={isGenerating}
                                        >
                                            Cambiar
                                        </Button>
                                    </Box>
                                </Box>
                            )}

                            {documentType === 'INVOICE' && invoiceType === 'A' && (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    <Typography variant="body2">
                                        <strong>Factura A (IVA Discriminado):</strong> Obligatorio ingresar datos del cliente. 
                                        El cliente debe ser <strong>Responsable Inscripto o Monotributo</strong>. 
                                        El IVA se muestra separado en el comprobante.
                                    </Typography>
                                </Alert>
                            )}
                        </Box>
                    )}

                    {/* Info alerts for Presupuesto (Factura X) */}
                    {documentType === 'INVOICE' && invoiceType === 'X' && (
                        <>
                            {/* AFIP Registration Status Alert */}
                            {INVOICE_FEATURES.PRESUPUESTO_REGISTER_WITH_AFIP ? (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    <Typography variant="body2">
                                        <strong>⚠️ Presupuesto con Registro AFIP:</strong> Este presupuesto será registrado 
                                        en AFIP y generará un CAE.
                                    </Typography>
                                </Alert>
                            ) : (
                                <Alert severity="success" sx={{ mt: 2 }}>
                                    <Typography variant="body2">
                                        <strong>✓ Factura X sin Registro AFIP:</strong> Este comprobante NO será registrado 
                                        en AFIP y NO generará CAE. Es una cotización interna para el cliente.
                                    </Typography>
                                </Alert>
                            )}
                            
                            {INVOICE_FEATURES.PRESUPUESTO_COPY_TO_CLIPBOARD && (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    <Typography variant="body2">
                                        <strong>📋 Copiar Datos:</strong> Al confirmar, los datos de la Factura X se copiarán 
                                        automáticamente al portapapeles para que puedas pegarlos en otro software.
                                    </Typography>
                                </Alert>
                            )}
                        </>
                    )}
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Invoice Header */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom color="primary">
                        Información General
                    </Typography>
                    <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                        gap: 2,
                        p: 2,
                        backgroundColor: '#f5f5f5',
                        borderRadius: 1
                    }}>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Tipo de Comprobante
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {invoiceType === 'A' && 'Factura A - Responsable Inscripto / Monotributo'}
                                {invoiceType === 'B' && 'Factura B - Consumidor Final / Exento'}
                                {invoiceType === 'C' && 'Factura C - Consumidor Final'}
                                {invoiceType === 'X' && 'Factura X'}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Fecha de Emisión
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {new Date().toLocaleDateString('es-AR', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric'
                                })}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Cliente
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                Consumidor Final
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Cantidad de Items
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {cartDetails.length}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Items Table */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom color="primary">
                        Detalle de Productos
                    </Typography>
                    <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Código</strong></TableCell>
                                    <TableCell><strong>Descripción</strong></TableCell>
                                    <TableCell align="center"><strong>Cantidad</strong></TableCell>
                                    <TableCell align="right"><strong>Precio Unit.</strong></TableCell>
                                    <TableCell align="right"><strong>Subtotal</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {cartDetails.map((item, index) => {
                                    const effectivePrice = getEffectivePrice(item);
                                    const splitter = item.splitter || 1;
                                    const pricePerUnit = effectivePrice / splitter;
                                    const itemSubtotal = pricePerUnit * (item.quantity || 0);
                                    return (
                                        <TableRow key={index} hover>
                                            <TableCell>{item.ProductCode || 'N/A'}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" noWrap>
                                                    {item.ProductDescription}
                                                </Typography>
                                                {item.BrandName && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {item.BrandName}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                {item.quantity}
                                                {item.splitter > 1 && (
                                                    <Typography variant="caption" display="block" color="text.secondary">
                                                        (/{item.splitter})
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="right">{formatPrice(pricePerUnit)}</TableCell>
                                            <TableCell align="right">
                                                <strong>{formatPrice(itemSubtotal)}</strong>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Financial Summary */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom color="primary">
                        Resumen Financiero
                    </Typography>
                    
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        gap: 2,
                        p: 3,
                        backgroundColor: '#f5f5f5',
                        borderRadius: 1
                    }}>
                        {/* AFIP-Compliant: Subtotal WITHOUT IVA */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body1">
                                Subtotal (Sin IVA):
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {formatPrice(breakdown.subtotalWithoutIVA)}
                            </Typography>
                        </Box>

                        {/* AFIP-Compliant: Discount applied to subtotal sin IVA */}
                        {discountPercentage > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body1" color="warning.main">
                                    Descuento ({discountPercentage}%):
                                </Typography>
                                <Typography variant="body1" fontWeight="bold" color="warning.main">
                                    -{formatPrice(breakdown.discountAmountSimple)}
                                </Typography>
                            </Box>
                        )}

                        {/* AFIP-Compliant: IVA calculated on discounted amount */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body1">
                                IVA (21%):
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {formatPrice(breakdown.ivaAmount)}
                            </Typography>
                        </Box>

                        <Divider />

                        {/* AFIP-Compliant: Total with IVA */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h5" color="success.main">
                                Total a Facturar:
                            </Typography>
                            <Typography variant="h4" fontWeight="bold" color="success.main">
                                {formatPrice(breakdown.finalTotal)}
                            </Typography>
                        </Box>
                        
                        {/* DUAL CALCULATION VALIDATION BADGE (NEW!) */}
                        {breakdown.isDual && breakdown.validationBadge?.show && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                <Chip 
                                    label={breakdown.validationBadge.text}
                                    color={breakdown.validationBadge.color}
                                    size="small"
                                    sx={{ 
                                        fontWeight: 'bold',
                                        fontSize: '0.75rem'
                                    }}
                                />
                            </Box>
                        )}
                        
                        {/* Calculation source indicator */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Typography variant="caption" color="text.secondary">
                                {breakdown.isDual 
                                    ? `🚀 DUAL: ${breakdown.calculationSource === 'frontend-instant' ? 'Instant preview' : 'Server validated'}`
                                    : `📊 ${breakdown.calculationSource || 'backend-v2'}`
                                }
                            </Typography>
                        </Box>
                        
                        {/* Mismatch warning (if detected) */}
                        {breakdown.isDual && breakdown.mismatchDetected && breakdown.mismatchDetails && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                <Typography variant="body2" fontWeight="bold" gutterBottom>
                                    ⚠️ Diferencia detectada
                                </Typography>
                                <Typography variant="body2">
                                    Tu cálculo: {formatPrice(breakdown.mismatchDetails.frontendTotal)}
                                </Typography>
                                <Typography variant="body2">
                                    Servidor: {formatPrice(breakdown.mismatchDetails.backendTotal)}
                                </Typography>
                                <Typography variant="body2" color="error" fontWeight="bold">
                                    Diferencia: {formatPrice(breakdown.mismatchDetails.difference)} ({breakdown.mismatchDetails.percentageDiff.toFixed(2)}%)
                                </Typography>
                                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                    Se usará el valor del servidor para garantizar cumplimiento tributario.
                                </Typography>
                            </Alert>
                        )}
                    </Box>
                </Box>

                {/* Warning about AFIP limits */}
                {documentType === 'INVOICE' && breakdown.finalTotal > 10000000 && invoiceType === 'B' && !selectedClient && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                            <strong>⚠️ Atención:</strong> El monto total excede los <strong>$10.000.000 ARS</strong>. 
                            Para Factura B, AFIP <strong>requiere los datos del cliente</strong> cuando el monto supera este límite. 
                            Por favor, busque y seleccione un cliente antes de continuar.
                        </Typography>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3, backgroundColor: '#f9f9f9' }}>
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                    width: '100%',
                    justifyContent: 'space-between'
                }}>
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        disabled={isGenerating}
                        sx={{ flex: 1 }}
                    >
                        Cancelar
                    </Button>
                    
                    <Button
                        variant="contained"
                        startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                        onClick={handleGenerateClick}
                        disabled={
                            isGenerating || 
                            cartDetails.length === 0 ||
                            (documentType === 'INVOICE' && invoiceType === 'A' && !selectedClient) ||
                            (documentType === 'RUNNING_ACCOUNT' && !selectedRunningAccountClient) ||
                            (documentType === 'RUNNING_ACCOUNT' && (!pickedUpBy || pickedUpBy.trim() === '')) ||
                            (breakdown.isDual && !breakdown.isValidated) || // CRITICAL: Block if dual calc not validated
                            (documentType === 'INVOICE' && paymentMode === 'split' && (
                                Math.abs((payment1.amount + payment2.amount) - breakdown.finalTotal) > 0.01 ||
                                payment1.method === payment2.method ||
                                payment1.amount <= 0 ||
                                payment2.amount <= 0
                            ))
                        }
                        sx={{ 
                            flex: 1,
                            backgroundColor: documentType === 'RUNNING_ACCOUNT'
                                ? '#ff9800'
                                : (isAlreadyInvoiced ? '#fa8c16' : '#47ba8c'),
                            '&:hover': {
                                backgroundColor: documentType === 'RUNNING_ACCOUNT'
                                    ? '#f57c00'
                                    : (isAlreadyInvoiced ? '#d46b08' : '#3a9572')
                            }
                        }}
                    >
                        {isGenerating 
                            ? (documentType === 'BUDGET_QUOTE'
                                ? 'Generando Presupuesto...'
                                : (documentType === 'RUNNING_ACCOUNT'
                                    ? 'Agregando a Cuenta...'
                                    : (invoiceType === 'X' 
                                        ? 'Generando...' 
                                        : 'Enviando a AFIP...')))
                            : (documentType === 'BUDGET_QUOTE'
                                ? '📋 Generar Presupuesto'
                                : (documentType === 'RUNNING_ACCOUNT'
                                    ? '📋 Confirmar y Cargar a Cuenta Abierta'
                                    : (invoiceType === 'X' 
                                        ? (isAlreadyInvoiced ? '📋 Generar e Imprimir Nuevamente' : '📋 Generar e Imprimir')
                                        : (isAlreadyInvoiced ? 'Confirmar y Enviar a AFIP Nuevamente' : 'Confirmar y Enviar a AFIP'))))
                        }
                    </Button>
                </Box>
            </DialogActions>
            
            {/* Client Selection Modal */}
            <ClientSelectionModal
                open={showClientModal}
                onClose={() => setShowClientModal(false)}
                clientData={clientSearchResult}
                onSelectWithoutSave={handleSelectClientWithoutSave}
                onSelectAndSave={handleSelectClientAndSave}
            />
        </Dialog>
        
        {/* Budget Quote Success Modal - Rendered outside main Dialog */}
        <BudgetQuoteSuccessModal
            open={isBudgetQuoteSuccessOpen}
            onClose={() => {
                setIsBudgetQuoteSuccessOpen(false);
                setBudgetQuoteData(null);
                onClose(); // Close parent modal too
            }}
            quoteData={budgetQuoteData}
            onClearCart={() => {
                setIsBudgetQuoteSuccessOpen(false);
                setBudgetQuoteData(null);
                // Clear the cart
                if (onClearCart) {
                    onClearCart();
                }
                onClose(); // Close parent modal
            }}
            onKeepCart={() => {
                setIsBudgetQuoteSuccessOpen(false);
                setBudgetQuoteData(null);
                onClose(); // Close parent modal
            }}
        />
        
        {/* Running Account Success Modal */}
        <RunningAccountSuccessModal
            open={isRunningAccountSuccessOpen}
            onClose={() => {
                setIsRunningAccountSuccessOpen(false);
                setRunningAccountSuccessData(null);
                onClose(); // Close parent modal too
            }}
            clientData={runningAccountSuccessData?.clientData}
            movementData={runningAccountSuccessData?.movementData}
            itemCount={runningAccountSuccessData?.itemCount}
            total={runningAccountSuccessData?.total}
            onClearCart={() => {
                setIsRunningAccountSuccessOpen(false);
                setRunningAccountSuccessData(null);
                if (onClearCart) {
                    onClearCart();
                }
                onClose();
            }}
            onKeepCart={() => {
                setIsRunningAccountSuccessOpen(false);
                setRunningAccountSuccessData(null);
                onClose();
            }}
            onGoToRunningAccount={(clearCart) => {
                setIsRunningAccountSuccessOpen(false);
                setRunningAccountSuccessData(null);
                if (clearCart && onClearCart) {
                    onClearCart();
                }
                onClose();
                // Navigate to Clientes page with cuentas-abiertas tab
                window.location.hash = '#/clientes?tab=cuentas-abiertas';
            }}
        />

        {/* ⚠️ Duplicate-invoice confirmation dialog */}
        <Dialog
            open={showDuplicateConfirm}
            onClose={() => setShowDuplicateConfirm(false)}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: 2 } }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
                <span style={{ fontSize: '20px' }}>⚠️</span>
                Factura duplicada
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                    Ya generaste una factura desde este carrito sin modificaciones
                    {lastInvoiceNumber && (
                        <>
                            {' '}(N°&nbsp;<strong>{lastInvoiceNumber}</strong>)
                        </>
                    )}.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    ¿Deseás generar una factura duplicada?
                </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
                <Button
                    variant="outlined"
                    onClick={() => setShowDuplicateConfirm(false)}
                    sx={{ flex: 1 }}
                >
                    NO
                </Button>
                <Button
                    variant="contained"
                    onClick={() => {
                        setShowDuplicateConfirm(false);
                        handleConfirmGeneration();
                    }}
                    sx={{
                        flex: 1,
                        backgroundColor: '#fa8c16',
                        '&:hover': { backgroundColor: '#d46b08' },
                    }}
                >
                    SÍ, GENERAR
                </Button>
            </DialogActions>
        </Dialog>
    </>
    );
}

InvoicePreviewModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    cartData: PropTypes.shape({
        cartDetails: PropTypes.arrayOf(PropTypes.shape({
            ProductId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            ProductCode: PropTypes.string,
            ProductDescription: PropTypes.string,
            BrandName: PropTypes.string,
            RetailPrice: PropTypes.number,
            quantity: PropTypes.number,
            splitter: PropTypes.number,
        })),
        subTotalPrice: PropTypes.number,
        totalAfterDiscount: PropTypes.number,
        discountPercentage: PropTypes.number,
    }).isRequired,
    getDiscountBreakdown: PropTypes.func, // NEW: Optional discount breakdown function
    onConfirmGeneration: PropTypes.func.isRequired,
    onClearCart: PropTypes.func,
    isAlreadyInvoiced: PropTypes.bool,
    lastInvoiceNumber: PropTypes.string,
};

export default InvoicePreviewModal;
